/**
 * TypeScript Type Definitions for City Autosuggest Feature
 * Contains interfaces for city search, suggestions, and API responses
 */

/**
 * City result from the API
 */
export interface CityResult {
  placeId: string;
  name: string;
  state: string;
  country: string;
  displayName: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
}

/**
 * Cached city result with metadata
 */
export interface CachedCityResult {
  query: string;
  results: CityResult[];
  timestamp: number;
  expiresAt: number;
}

/**
 * City search API response
 */
export interface CitySearchResponse {
  success: boolean;
  data?: {
    cities: CityResult[];
    cached: boolean;
    timestamp: string;
  };
  error?: {
    code: CityAutosuggestError;
    message: string;
  };
}

/**
 * Error types for city autosuggest
 */
export enum CityAutosuggestError {
  API_UNAVAILABLE = 'API_UNAVAILABLE',
  QUOTA_EXCEEDED = 'QUOTA_EXCEEDED',
  NETWORK_ERROR = 'NETWORK_ERROR',
  INVALID_QUERY = 'INVALID_QUERY',
  TIMEOUT = 'TIMEOUT'
}

/**
 * Props for CityAutosuggest component
 */
export interface CityAutosuggestProps {
  value: string;
  onChange: (city: string) => void;
  onSelect?: (city: CityResult) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  enableAutosuggest?: boolean;
  apiEndpoint?: string;
  debounceMs?: number;
  minCharacters?: number;
  maxSuggestions?: number;
  error?: string;
  'data-testid'?: string;
}

/**
 * State for CityAutosuggest component
 */
export interface CityAutosuggestState {
  inputValue: string;
  suggestions: CityResult[];
  isLoading: boolean;
  isOpen: boolean;
  selectedIndex: number;
  error: string | null;
  cache: Map<string, CachedCityResult>;
}

/**
 * Configuration for city autosuggest
 */
export interface CityAutosuggestConfig {
  debounceMs: number;
  minCharacters: number;
  maxSuggestions: number;
  cacheExpiryMs: number;
  apiTimeoutMs: number;
}

/**
 * Default configuration
 */
export const DEFAULT_CITY_AUTOSUGGEST_CONFIG: CityAutosuggestConfig = {
  debounceMs: 300,
  minCharacters: 3,
  maxSuggestions: 10,
  cacheExpiryMs: 60 * 60 * 1000, // 1 hour
  apiTimeoutMs: 3000 // 3 seconds
};